import { FC, useEffect, useRef, useState } from 'react';

import logger from '@eneco/common/log';
import { stripAccolades } from '@eneco/common/string';
import { Recaptcha, RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { RecaptchaForm } from '@eneco/components/RecaptchaForm/RecaptchaForm';
import RichText from '@eneco/components/RichText/RichText';
import { useFormsPublicV2PostB2BForm } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Button, InputText, Stack, Stretch, Text, TextLink, TransitionOpacity } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { Placeholder, useLayoutData } from '@sitecore/common';
import { NewsletterFormIntegratedWithRevolutionRendering } from '@sitecore/types/NewsletterFormIntegratedWithRevolution';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';

import { getConfiguredFieldValue } from './utils';
import { prepareRevolutionFormRequest } from '../GenericForm/util/request';
import type { FormValues } from '../GenericForm/util/types';

const CONSENT_FIELD_NAME = 'msdynmkt_purposeid;channels;optinwhenchecked;msdynmkt_value';

const newsletterFormSchema = yup.object({
  emailaddress1: yup.string().email().required(),
  recaptchaToken: yup.string().required(),
  formId: yup.string().required(),
  enc_formtype: yup.string().required(),
  leadsourcecode: yup.string().required(),
  [CONSENT_FIELD_NAME]: yup.string().required(),
});

interface NewsletterFormValues extends FormValues {
  emailaddress1: string;
  recaptchaToken: string;
  formId: string;
  enc_formtype: string;
  leadsourcecode: string;
  [CONSENT_FIELD_NAME]: string;
}

export const NewsletterFormIntegrateWithRevolutionCRM: FC<NewsletterFormIntegratedWithRevolutionRendering> = ({
  fields,
}) => {
  const { label, businessUnit } = useDC();
  const {
    context: { pageEditing },
  } = useLayoutData();
  const { trackFormInteractionStart, trackFormInteractionCompleted } = useTracking();

  const {
    formFields: { emailAddressFormField: { value: emailAddressValue } = {} },
    footer: { disclaimerContent, backButtonText, nextButtonText },
    settings,
  } = fields;

  const formSettings = {
    enc_formtype: getConfiguredFieldValue(settings.encFormTypeText),
    leadsourcecode: getConfiguredFieldValue(settings.leadSourceCodeText),
    formId: getConfiguredFieldValue(settings.formIdOption),
    [CONSENT_FIELD_NAME]: getConfiguredFieldValue(settings.consentIdOption),
  };

  const recaptchaRef = useRef<RecaptchaRef>(null);
  const [isTransitioning, setTransitioning] = useState(false);
  const { send, reset, isError, isSuccess } = useFormsPublicV2PostB2BForm();

  const methods = useForm<NewsletterFormValues>({
    mode: 'onBlur',
    defaultValues: {
      formId: formSettings.formId,
      enc_formtype: formSettings.enc_formtype,
      leadsourcecode: formSettings.leadsourcecode,
      [CONSENT_FIELD_NAME]: formSettings[CONSENT_FIELD_NAME],
    },
    resolver: typedYupResolver<NewsletterFormValues>(newsletterFormSchema),
  });
  const {
    formState: { errors },
    register,
  } = methods;

  const submitForm: SubmitHandler<NewsletterFormValues> = async values => {
    setTransitioning(true);

    try {
      const formId = typeof values.formId === 'string' ? values.formId : '';
      const requestBody = prepareRevolutionFormRequest(formId, values);

      await send({
        businessUnit,
        formId: stripAccolades(formId),
        label,
        requestBody,
        recaptchaEnterpriseToken: values.recaptchaToken,
      } as any); // eslint-disable-line @typescript-eslint/no-explicit-any
    } catch (error) {
      logger.error('KSK1W4', 'Failed to submit form', { error });
    } finally {
      setTimeout(() => {
        setTransitioning(false);
      }, 500);
    }
  };

  const getEmailErrorMessage = () => {
    const defaultErrorMessage = errors.emailaddress1?.message;
    const requiredMessage = emailAddressValue?.requiredMessage;
    const validationMessage = emailAddressValue?.validationMessage;

    if (errors.emailaddress1?.type === 'required') {
      return requiredMessage ?? defaultErrorMessage;
    } else if (errors.emailaddress1?.type === 'email' && validationMessage) {
      return validationMessage ?? defaultErrorMessage;
    }

    return undefined;
  };

  useEffect(() => {
    if (isSuccess) {
      trackFormInteractionCompleted();
    }
  }, [isSuccess, trackFormInteractionCompleted]);

  const formOutput = (
    <FormProvider {...methods}>
      <RecaptchaForm onSubmit={submitForm} recaptchaRef={recaptchaRef} onFocus={trackFormInteractionStart}>
        <Stack gap="6">
          <Stack
            direction={{ initial: 'column', md: 'row' }}
            gap="6"
            alignY="justify"
            alignX={{ initial: undefined, md: 'justify' }}>
            <Stack.Item grow>
              <InputText
                {...register('emailaddress1')}
                label={emailAddressValue?.label ?? 'Email'}
                placeholder={emailAddressValue?.placeholder ?? 'mijn@email.nl'}
                hint={emailAddressValue?.hint}
                error={getEmailErrorMessage()}
              />
            </Stack.Item>

            <input type="hidden" {...register(CONSENT_FIELD_NAME)} />
            <input type="hidden" {...register('formId')} />
            <input type="hidden" {...register('enc_formtype')} />
            <input type="hidden" {...register('leadsourcecode')} />

            <Box paddingTop={{ md: '7' }}>
              <Stretch width={{ initial: true, md: false }} height={false}>
                <Button size="compact" type="submit">
                  {nextButtonText?.value}
                </Button>
              </Stretch>
            </Box>
          </Stack>
          <Text size="BodyS">
            <RichText html={disclaimerContent?.value} />
          </Text>
          <Text size="BodyS">
            <Recaptcha hasError={!!errors.recaptchaToken} ref={recaptchaRef} />
          </Text>
        </Stack>
      </RecaptchaForm>
    </FormProvider>
  );

  const showForm = !isSuccess && !isError;
  const showBackButton = isSuccess || isError;

  return (
    <Box
      backgroundColor="backgroundSecondary"
      borderColor="borderDividerMediumEmphasis"
      borderRadius="m"
      borderWidth="s"
      padding={{
        initial: 6,
        lg: 10,
      }}>
      <TransitionOpacity in={!isTransitioning}>
        <Stack gap="6">
          {showForm && (
            <>
              <Placeholder name="jss-newsletter-form-intro" />
              {formOutput}
            </>
          )}

          {isSuccess || pageEditing ? (
            <>
              {pageEditing && 'This is the success message placeholder'}
              <Placeholder name="jss-newsletter-form-success" />
            </>
          ) : null}

          {isError || pageEditing ? (
            <>
              {pageEditing && 'This is the error message placeholder'}
              <Placeholder name="jss-newsletter-form-error" />
            </>
          ) : null}

          {showBackButton && (
            <TextLink
              type="button"
              onClick={() => {
                setTransitioning(true);
                setTimeout(() => {
                  reset();
                  setTransitioning(false);
                }, 500);
              }}
              emphasis="high">
              {backButtonText?.value}
            </TextLink>
          )}
        </Stack>
      </TransitionOpacity>
    </Box>
  );
};

export default NewsletterFormIntegrateWithRevolutionCRM;
